import { InfoRow, InfoSheet, loadCoachInfo, type CoachView } from '../components/CoachInfo';
import { PinnedSheet, SetRow } from '../components/SetSheet';
import { DrillLibrarySheet } from '../components/Drills';
import { BriefRow } from '../components/Brief';
import { useEffect, useState, type ReactNode } from 'react';
import { PlanRow, type Billing } from './Billing';
import { getLang, localeOf, useT } from '../lib/i18n';
import { GrowArea } from '../components/GrowArea';
import { nudgeSample, NudgeBubble } from '../components/Regulars';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { TagsSheet } from '../components/TagSheet';
import { AxesSheet } from '../components/AxisSheet';
import { createPortal } from 'react-dom';
import { alert, confirm, haptic, openExternal } from '../lib/telegram';
import { subscribeToCalendar } from './Screens';
import { TimeOffTab } from './TimeOff';

/**
 * Settings.
 *
 * A menu, not a scroll: four groups of rows, each row carrying what is
 * set right now («Пн–Сб 07–13, 15–19 · Вс 09–14»), and the fields behind
 * it on their own screen. The screens are pushed over the menu — the
 * «‹ Настройки» row and Telegram's own back arrow both go back — and
 * nothing is remembered, so closing the app and opening it again lands
 * on the menu.
 *
 * Everything here used to require me sending you SQL. The reassuring part,
 * worth saying on screen: changing a price never touches what's already
 * sold. Someone holding a 10-pack keeps the rate they paid.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let token = '';
export const setSettingsToken = (t: string) => { token = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    // No body, no content type: the server reads the header as a promise
    // of JSON and refuses the request when none arrives.
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

interface SessionType {
  id: string; kind: string; name: string; duration_min: number;
  cash_price: number | null; credit_cost: number; active: boolean;
  /** sql/046: the lesson's sport, fixed once the type has lessons (`used`). */
  sport?: Sport; used?: boolean;
}
type Sport = 'padel' | 'tennis';
interface Pkg {
  id: string; name: string; credits: number; price: number;
  validity_days: number | null; active: boolean; sold: number;
}
interface Hour { id: string; weekday: number; start_time: string; end_time: string }
interface Coach {
  require_court?: boolean;
  /** sql/046: padel, or padel and tennis. */
  sports?: Sport[];
  display_name: string; court_cost_per_hour: number; cancel_cutoff_hours: number;
  match_review_enabled?: boolean; match_review_fee?: number;
  min_notice_hours: number; booking_horizon_days: number;
  reminder_hours?: number; cancel_warning_hours?: number;
  /** sql/050, «Напоминания»: 'HH:MM'. */
  reminder_cutoff?: string; reminder_evening_at?: string; reminder_day_at?: string;
  clients_can_invite?: boolean;
  approve_new_clients?: boolean;
  /** «Постоянные» (sql/037): the threshold, the switch and the coach's wording. */
  regular_runway_days?: number;
  /** sql/044: how far ahead ★ regulars book, in days; null — like everyone. */
  regular_horizon_days?: number | null;
  regular_nudge_mode?: 'off' | 'manual' | 'auto';
  regular_nudge_text?: string | null;
  /** «Неделя 1 начинается» (sql/042): always a Monday, or null for ISO weeks. */
  court_week1_start?: string | null;
}
interface Data {
  coach: Coach; sessionTypes: SessionType[]; packages: Pkg[]; hours: Hour[]; courtBands: any[];
}
/** «Первые шаги» answers whether the client link has ever been sent. */
interface FirstSteps { steps: { link: boolean }; link: string | null; slug?: string;
  /** «?start=<slug>-tennis», for a coach who teaches tennis too (coach/sports.ts). */
  tennisLink?: string | null }

const ANY_DAY = -1;

const DAY_KEYS = ['co.day.sunday', 'co.day.monday', 'co.day.tuesday', 'co.day.wednesday',
  'co.day.thursday', 'co.day.friday', 'co.day.saturday'] as const;
/** Monday first, the way a week is read here; Sunday last. */
const WEEK = [1, 2, 3, 4, 5, 6, 0] as const;

/** «Пн», «Сб» — from the phone's own locale, 5 January 2026 being a Monday. */
const shortDay = (weekday: number) => {
  const d = new Date(2026, 0, 4 + weekday);
  const s = new Intl.DateTimeFormat(localeOf(getLang()), { weekday: 'short' }).format(d).replace(/\.$/, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const hhmm = (t: string) => t.slice(0, 5);
/** «07:00–13:00» as «07–13»: the minutes only when there are any. */
const span = (start: string, end: string) => {
  const tidy = (v: string) => (v.endsWith(':00') ? v.slice(0, 2) : v);
  return `${tidy(hhmm(start))}–${tidy(hhmm(end))}`;
};

// ---------------------------------------------------------------------
//  The menu's one-line summaries, read off the current values
// ---------------------------------------------------------------------

/** «Пн–Сб 07–13, 15–19 · Вс 09–14»: days sharing the same hours, together. */
function summariseHours(hours: { weekday: number; start_time: string; end_time: string }[]) {
  const byDay = new Map<number, string>();
  for (const d of WEEK) {
    const list = hours.filter((h) => h.weekday === d)
      .sort((a, b) => a.start_time.localeCompare(b.start_time))
      .map((h) => span(h.start_time, h.end_time));
    if (list.length) byDay.set(d, list.join(', '));
  }
  if (byDay.size === 0) return null;
  // Consecutive days with identical hours read as a range.
  const parts: string[] = [];
  let run: number[] = [];
  const flush = () => {
    if (!run.length) return;
    const label = run.length === 1 ? shortDay(run[0])
      : run.length === 2 ? `${shortDay(run[0])}, ${shortDay(run[run.length - 1])}`
      : `${shortDay(run[0])}–${shortDay(run[run.length - 1])}`;
    parts.push(`${label} ${byDay.get(run[0])}`);
    run = [];
  };
  for (const d of WEEK) {
    if (!byDay.has(d)) { flush(); continue; }
    if (run.length && byDay.get(run[run.length - 1]) !== byDay.get(d)) flush();
    run.push(d);
  }
  flush();
  return parts.join(' · ');
}

// ---------------------------------------------------------------------
//  A screen pushed over the menu
// ---------------------------------------------------------------------

function SubScreen({ title, lead, onBack, children }: {
  title: string; lead?: ReactNode; onBack: () => void; children: ReactNode;
}) {
  const t = useT();
  // Opened from the menu, so the top of it is what should be on screen.
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="subscr" data-layer>
      <button className="subscr__back" data-back onClick={() => { haptic.tap(); onBack(); }}>‹ {t('co.tab.setup')}</button>
      <div className="subscr__title">{title}</div>
      {lead && <div className="subscr__lead">{lead}</div>}
      {children}
    </div>
  );
}

/**
 * Peak court pricing, with the court's own price.
 *
 * The base rate covers most hours; bands are the exceptions. One rate
 * applies to any minute, never a sum: a band for that weekday wins over an
 * every-day band, the highest rate among the same kind (coach/court-cost.ts).
 * A session crossing a boundary is charged pro rata, so 09:30 to 10:30 with
 * peak ending at 10:00 costs half an hour of each.
 *
 * A chip each, because a band is three short facts — when, which days,
 * how much — and a coach has two or three of them.
 */

/** «Каждый день 08:00–10:00 · 30 €». */
function bandLabel(t: ReturnType<typeof useT>, b: any) {
  const when = b.weekday === null ? t('co.set.everyDay') : shortDay(b.weekday);
  return `${when} ${hhmm(b.start_time)}–${hhmm(b.end_time)} · ${t('co.set.bandPerHour', { price: b.price_per_hour })}`;
}

/** One band: the same fields whether it is new or already there. */
function BandSheet({ band, bands, base, onClose, onChanged }: {
  band: any | null; bands: any[]; base: number; onClose: () => void; onChanged: () => void;
}) {
  const t = useT();
  const [start, setStart] = useState(band ? hhmm(band.start_time) : '08:00');
  const [end, setEnd] = useState(band ? hhmm(band.end_time) : '10:00');
  const [price, setPrice] = useState(String(band ? band.price_per_hour : base));
  const [weekday, setWeekday] = useState<number>(band ? (band.weekday ?? ANY_DAY) : ANY_DAY);
  const [busy, setBusy] = useState(false);

  // Bands this one would overlap: the hours cross, on a day both apply to.
  const overlaps = start < end ? bands.filter((b) =>
    b.id !== band?.id
    && (b.weekday === null || weekday === ANY_DAY || b.weekday === weekday)
    && start < hhmm(b.end_time) && hhmm(b.start_time) < end) : [];

  async function save() {
    if (end <= start) { await alert(t('co.set.endAfterStart')); return; }
    setBusy(true);
    const body = JSON.stringify({
      start_time: start, end_time: end,
      price_per_hour: Number(price),
      weekday: weekday === ANY_DAY ? null : weekday,
    });
    try {
      await (band ? api(`/api/coach/court-bands/${band.id}`, { method: 'PUT', body })
        : api('/api/coach/court-bands', { method: 'POST', body }));
      haptic.success();
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.set.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!(await confirm(t('co.set.removeRate')))) return;
    setBusy(true);
    try {
      await api(`/api/coach/court-bands/${band.id}`, { method: 'DELETE' });
      haptic.success();
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.set.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <PinnedSheet title={t('co.set.bandTitle')} onClose={onClose}
      footer={<>
        {band
          ? <button className="paybar__opt" disabled={busy} onClick={remove}>{t('co.set.remove')}</button>
          : <button className="paybar__opt" onClick={onClose}>{t('co.set.close')}</button>}
        <button className="savebtn" style={{ marginTop: 0 }} disabled={busy} onClick={save}>
          {band ? t('co.set.save') : t('co.set.addRate')}
        </button>
      </>}>
      <div className="hoursint">
        <label className="hoursint__f"><span>{t('co.set.from')}</span>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label className="hoursint__f"><span>{t('co.set.until')}</span>
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
      </div>
      <select className="paybar__opt" style={{ width: '100%', textAlign: 'left', margin: '10px 0' }}
        value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
        <option value={ANY_DAY}>{t('co.set.everyDay')}</option>
        {DAY_KEYS.map((k, n) => (
          <option key={n} value={n}>{t('co.set.dayOnly', { day: t(k) })}</option>
        ))}
      </select>
      <Field label={t('co.set.courtCosts')} value={price} onChange={setPrice} suffix={t('co.set.perHour')} />
      {/* One rate applies to any minute (coach/court-cost.ts): say so when this one overlaps another. */}
      {overlaps.length > 0 && (
        <div className="warnline" style={{ marginTop: 8 }}>
          {t('co.set.bandOverlap', { band: overlaps.map((b) => bandLabel(t, b)).join(', ') })}
        </div>
      )}
    </PinnedSheet>
  );
}

/**
 * «Неделя 1 начинается» (coaches.court_week1_start): where the club's
 * «WEEK nn» in the court bill export counts from. The server keeps the
 * Monday of the week entered, and that Monday is what shows here, so the
 * blocks are always Monday to Sunday. Empty: the ISO week number.
 */
function Week1Start({ value, onChanged }: { value: string | null; onChanged: () => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  async function save(date: string | null) {
    setBusy(true);
    try {
      await api('/api/coach/court-week1', { method: 'PUT', body: JSON.stringify({ date }) });
      haptic.success();
      onChanged();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.set.failed', { message: err.message }));
    } finally { setBusy(false); }
  }
  const monday = value
    ? new Intl.DateTimeFormat(localeOf(getLang()), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
        .format(new Date(`${value}T12:00:00Z`)).replace(/ г\.$/, '')
    : null;
  return (
    <div className="week1">
      <label className="week1__row">
        <span>{t('co.set.week1')}</span>
        <input type="date" value={value ?? ''} disabled={busy}
          onChange={(e) => { if (e.target.value) save(e.target.value); }} />
      </label>
      {value && <button className="week1__clear" disabled={busy} onClick={() => save(null)}>{t('co.set.week1Clear')}</button>}
      <div className="card__when">{monday ? t('co.set.week1Norm', { date: monday }) : t('co.set.week1Hint')}</div>
    </div>
  );
}

function CourtBands({ bands, base, week1, onChanged }: {
  bands: any[]; base: number; week1: string | null; onChanged: () => void;
}) {
  const t = useT();
  const [sheet, setSheet] = useState<{ band: any | null } | null>(null);
  return (
    <>
      <div className="section-title">{t('co.set.titlePeak')}</div>
      <div className="list">
        <div className="card card--pad">
          <div className="hoursday__chips">
            {bands.map((b) => (
              <button key={b.id} className="hchip hchip--peak" onClick={() => { haptic.tap(); setSheet({ band: b }); }}>
                {bandLabel(t, b)}
              </button>
            ))}
            <button className="hchip hchip--add" onClick={() => { haptic.tap(); setSheet({ band: null }); }}>
              {t('co.set.addPeakShort')}
            </button>
          </div>
          <div className="card__when" style={{ marginTop: 10 }}>
            {bands.length === 0 ? t('co.set.peakNone', { base }) : t('co.set.splitNote')}
          </div>
          <Week1Start value={week1} onChanged={onChanged} />
        </div>
      </div>
      {sheet && (
        <BandSheet band={sheet.band} bands={bands} base={base}
          onClose={() => setSheet(null)}
          onChanged={() => { setSheet(null); onChanged(); }} />
      )}
    </>
  );
}

/**
 * All upcoming sessions into the coach's own calendar, in two taps.
 *
 * The token is fetched first so the button can hand a plain URL to the
 * phone's calendar handler — a fetch with an auth header would return
 * the file to JavaScript, which is no use.
 */
function SendToCalendar() {
  const t = useT();
  const [href, setHref] = useState<string | null>(null);
  const [webcal, setWebcal] = useState<string | null>(null);

  useEffect(() => {
    api<{ url: string; webcal: string }>('/api/coach/calendar-url')
      .then((r) => {
        setWebcal(r.webcal);
        const token = r.url.split('/').pop()?.replace('.ics', '') ?? '';
        setHref(`${import.meta.env.VITE_API_URL ?? ''}/api/coach/calendar/sessions.ics?token=${token}`);
      })
      .catch(() => setHref(null));
  }, []);

  if (href === null) return <div className="list"><div className="skeleton" /></div>;

  return (
    <div className="list">
      <div className="card">
        {/* Subscribing keeps past sessions and drops cancelled ones by itself. */}
        {webcal && <>
          <button className="savebtn" style={{ marginTop: 0 }}
            onClick={() => { haptic.tap(); subscribeToCalendar(webcal); }}>
            {t('co.set.subscribe')}
          </button>
          <div className="card__when" style={{ marginTop: 10, marginBottom: 14 }}>
            {t('co.set.subscribeHint')}
          </div>
        </>}
        <button className="savebtn savebtn--ghost" style={{ marginTop: 0 }}
          onClick={() => { haptic.tap(); openExternal(href); }}>
          {t('co.set.sendToCalendar')}
        </button>
        <div className="card__when" style={{ marginTop: 10 }}>
          {t('co.set.sendToCalendarHint')}
        </div>
      </div>
    </div>
  );
}

/**
 * The courts you are holding.
 *
 * Availability used to mean "the coach is free". But the coach does not
 * own the courts — they book a block at the club and then sell it, so an
 * hour with no court is not available at any price.
 *
 * 105 cells in a week is far too many to tap one at a time, so an hour
 * label toggles that hour across the week, a day label toggles the day,
 * and last week can be copied forward. Most weeks are the week before
 * with two changes.
 */
function CourtGrid({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [held, setHeld] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const HOURS = Array.from({ length: 15 }, (_, i) => 6 + i);
  const pad = (n: number) => String(n).padStart(2, '0');
  const dayKey = (i: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const cell = (dayIdx: number, hour: number) => `${dayKey(dayIdx)}T${pad(hour)}:00`;

  const load = async () => {
    const from = dayKey(0);
    const to = dayKey(6);
    try {
      const rows = await api<any[]>(`/api/coach/court-slots?from=${from}&to=${to}`);
      const next = new Set<string>();
      for (const r of rows) {
        const d = new Date(r.starts_at);
        next.add(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`);
      }
      setHeld(next);
      setDirty(new Set());
    } catch { setHeld(new Set()); }
  };
  useEffect(() => { load(); }, [weekStart.getTime()]);

  const toggle = (keys: string[]) => {
    haptic.tap();
    const next = new Set(held);
    // If any in the group are off, turn them all on. Otherwise all off.
    const anyOff = keys.some((k) => !next.has(k));
    for (const k of keys) { anyOff ? next.add(k) : next.delete(k); }
    setHeld(next);
    const d = new Set(dirty);
    for (const k of keys) d.add(k.slice(0, 10));
    setDirty(d);
  };

  async function save() {
    setBusy(true);
    try {
      // Per day, so a dropped connection cannot half-write a week.
      for (const date of dirty) {
        const hours = HOURS
          .filter((h) => held.has(`${date}T${pad(h)}:00`))
          .map((h) => `${pad(h)}:00`);
        await api('/api/coach/court-slots', {
          method: 'PUT', body: JSON.stringify({ date, hours }),
        });
      }
      haptic.success();
      await alert(t('co.set.saved'));
      setDirty(new Set());
    } catch (err: any) {
      haptic.error();
      await alert(t('co.set.couldNotSave', { message: err.message }));
    } finally { setBusy(false); }
  }

  async function copyLastWeek() {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    const p = `${prev.getFullYear()}-${pad(prev.getMonth() + 1)}-${pad(prev.getDate())}`;
    try {
      const res = await api<{ copied: number }>('/api/coach/court-slots/copy-week', {
        method: 'POST', body: JSON.stringify({ from_week: p, to_week: dayKey(0) }),
      });
      haptic.success();
      await alert(res.copied > 0 ? t('co.set.hoursCopied', { n: res.copied }) : t('co.set.lastWeekEmpty'));
      load();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.set.couldNotCopy', { message: err.message }));
    }
  }

  const label = new Intl.DateTimeFormat(localeOf(getLang()), { day: 'numeric', month: 'short' });
  // Monday first; 5 January 2026 was a Monday.
  const DAYS = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(localeOf(getLang()), { weekday: 'narrow' }).format(new Date(2026, 0, 5 + i)));

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '92vh' }}>
        <div className="sheet__title">{t('co.set.courtsTitle')}</div>
        <div className="card__when">{t('co.set.courtsHint')}</div>

        <div className="monthnav" style={{ padding: '10px 0 6px' }}>
          <button onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}>‹</button>
          <span>{label.format(weekStart)} – {label.format(new Date(weekStart.getTime() + 6 * 86400000))}</span>
          <button onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}>›</button>
        </div>

        <div className="courtgrid">
          <div />
          {DAYS.map((d, i) => (
            <button key={i} className="courtgrid__head"
              onClick={() => toggle(HOURS.map((h) => cell(i, h)))}>
              {d}<span>{new Date(weekStart.getTime() + i * 86400000).getDate()}</span>
            </button>
          ))}

          {HOURS.map((h) => (
            <>
              <button key={`h${h}`} className="courtgrid__hour"
                onClick={() => toggle(DAYS.map((_, i) => cell(i, h)))}>
                {pad(h)}
              </button>
              {DAYS.map((_, i) => {
                const k = cell(i, h);
                const on = held.has(k);
                return (
                  <button key={k}
                    className={`courtgrid__cell${on ? ' courtgrid__cell--on' : ''}`}
                    onClick={() => toggle([k])} />
                );
              })}
            </>
          ))}
        </div>

        <button className="paybar__opt" style={{ width: '100%', marginTop: 12 }}
          onClick={copyLastWeek}>{t('co.set.copyLastWeek')}</button>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="paybar__opt" style={{ flex: 1 }} onClick={onClose}>{t('co.set.close')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
            disabled={busy || dirty.size === 0} onClick={save}>
            {busy ? t('co.set.saving')
              : dirty.size ? t('co.set.saveDays', { n: dirty.size, days: t.word('co.days', dirty.size) })
              : t('co.set.savedShort')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------
//  Working hours: a row per day, intervals as chips
// ---------------------------------------------------------------------

interface Slot { weekday: number; start_time: string; end_time: string }
const sameHours = (a: Slot[], b: Slot[]) => JSON.stringify(a) === JSON.stringify(b);
const sortSlots = (rows: Slot[]) => [...rows].sort((a, b) =>
  WEEK.indexOf(a.weekday as any) - WEEK.indexOf(b.weekday as any) || a.start_time.localeCompare(b.start_time));

/** One interval, opened by tapping a chip or «+». */
function IntervalSheet({ slot, onSave, onRemove, onClose }: {
  slot: Slot; onSave: (s: Slot) => void; onRemove: (() => void) | null; onClose: () => void;
}) {
  const t = useT();
  const [start, setStart] = useState(slot.start_time);
  const [end, setEnd] = useState(slot.end_time);
  async function save() {
    if (end <= start) { await alert(t('co.set.endAfterStart')); return; }
    onSave({ weekday: slot.weekday, start_time: start, end_time: end });
  }
  return (
    <PinnedSheet title={`${t(DAY_KEYS[slot.weekday])} · ${t('co.set.interval')}`} onClose={onClose}
      footer={<>
        {onRemove && (
          <button className="paybar__opt" onClick={() => { haptic.tap(); onRemove(); }}>{t('co.set.remove')}</button>
        )}
        <button className="savebtn" style={{ marginTop: 0 }} onClick={save}>{t('co.set.done')}</button>
      </>}>
      <div className="hoursint">
        <label className="hoursint__f"><span>{t('co.set.from')}</span>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></label>
        <label className="hoursint__f"><span>{t('co.set.until')}</span>
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></label>
      </div>
      <div className="card__when" style={{ marginTop: 10 }}>{t('co.set.intervalHint')}</div>
    </PinnedSheet>
  );
}

function HoursScreen({ data, onBack, onSaved }: {
  data: Data; onBack: () => void; onSaved: () => void;
}) {
  const t = useT();
  const initial = sortSlots(data.hours.map((h) => ({
    weekday: h.weekday, start_time: hhmm(h.start_time), end_time: hhmm(h.end_time),
  })));
  const [rows, setRows] = useState<Slot[]>(initial);
  const [editing, setEditing] = useState<{ slot: Slot; at: number | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = !sameHours(rows, initial);

  /** A day's intervals, in the order they are drawn. */
  const ofDay = (d: number) => rows
    .map((r, i) => ({ r, i }))
    .filter((x) => x.r.weekday === d)
    .sort((a, b) => a.r.start_time.localeCompare(b.r.start_time));

  function put(slot: Slot, at: number | null) {
    setRows(sortSlots(at === null ? [...rows, slot] : rows.map((r, i) => (i === at ? slot : r))));
    setEditing(null);
  }
  function drop(at: number) {
    setRows(rows.filter((_, i) => i !== at));
    setEditing(null);
  }

  /** Monday's intervals onto Tuesday–Friday, the usual shape of a week. */
  async function copyMonday() {
    const mon = rows.filter((r) => r.weekday === 1);
    if (!mon.length) { await alert(t('co.set.copyMonEmpty')); return; }
    if (!(await confirm(t('co.set.copyMonConfirm')))) return;
    haptic.tap();
    setRows(sortSlots([
      ...rows.filter((r) => ![2, 3, 4, 5].includes(r.weekday)),
      ...[2, 3, 4, 5].flatMap((d) => mon.map((m) => ({ ...m, weekday: d }))),
    ]));
  }

  async function save() {
    if (!(await confirm(t('co.set.confirmHours')))) return;
    setBusy(true);
    try {
      await api('/api/coach/hours', { method: 'PUT', body: JSON.stringify({ hours: rows }) });
      haptic.success();
      await alert(t('co.set.hoursUpdated'));
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <SubScreen title={t('co.set.titleHours')} lead={t('co.set.hoursLead')} onBack={onBack}>
      <div className="list">
        <div className="card hoursday__card">
          {WEEK.map((d) => {
            const day = ofDay(d);
            return (
              <div className="hoursday" key={d}>
                <span className={`hoursday__name${day.length ? '' : ' hoursday__name--off'}`}>{shortDay(d)}</span>
                <span className="hoursday__chips">
                  {day.length === 0 && <span className="hchip hchip--off">{t('co.set.dayOff')}</span>}
                  {day.map(({ r, i }) => (
                    <button key={i} className="hchip"
                      onClick={() => { haptic.tap(); setEditing({ slot: r, at: i }); }}>
                      {span(r.start_time, r.end_time)}
                    </button>
                  ))}
                  <button className="hchip hchip--add" aria-label={t('co.set.addInterval')}
                    onClick={() => {
                      haptic.tap();
                      const last = day[day.length - 1]?.r;
                      setEditing({
                        slot: { weekday: d, start_time: last ? last.end_time : '07:00', end_time: last ? '21:00' : '11:00' },
                        at: null,
                      });
                    }}>+</button>
                </span>
              </div>
            );
          })}
        </div>
        <button className="savebtn savebtn--ghost" onClick={copyMonday}>{t('co.set.copyMon')}</button>
        {dirty && (
          <button className="savebtn" disabled={busy} onClick={save}>
            {busy ? t('co.set.saving') : t('co.set.saveHours')}
          </button>
        )}
        <div className="card__when" style={{ padding: '8px 2px' }}>{t('co.set.hoursNote')}</div>
      </div>


      {/* The other answer to "when am I free": the only screen it has. */}
      <TimeOffTab />

      {editing && (
        <IntervalSheet slot={editing.slot} onClose={() => setEditing(null)}
          onSave={(s) => put(s, editing.at)}
          onRemove={editing.at === null ? null : () => drop(editing.at!)} />
      )}
    </SubScreen>
  );
}

// ---------------------------------------------------------------------
//  Prices and packages: two lists, a sheet per row
// ---------------------------------------------------------------------

function SessionTypeSheet({ type, sports, onClose, onSaved }: {
  type: SessionType | null; sports: Sport[]; onClose: () => void; onSaved: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(type?.name ?? '');
  const [price, setPrice] = useState(String(type?.cash_price ?? ''));
  const [mins, setMins] = useState(type ? String(type.duration_min) : '60');
  // A new type: padel, or tennis for a tennis-only coach (as the server defaults it).
  const [sport, setSport] = useState<Sport>(type?.sport ?? (sports.includes('padel') ? 'padel' : 'tennis'));
  const [busy, setBusy] = useState(false);
  const dirty = !type || price !== String(type.cash_price ?? '') || mins !== String(type.duration_min)
    || sport !== (type.sport ?? 'padel');
  const ready = type ? dirty : name.trim() !== '' && Number(mins) > 0;

  async function save() {
    setBusy(true);
    try {
      // The same fields either way: a new session type is a POST, an
      // existing one the PUT it has always been.
      await (type
        ? api(`/api/coach/session-types/${type.id}`, {
          method: 'PUT', body: JSON.stringify({ cash_price: Number(price), duration_min: Number(mins),
            ...(sport !== (type.sport ?? 'padel') ? { sport } : {}) }),
        })
        : api('/api/coach/session-types', {
          method: 'POST',
          body: JSON.stringify({
            name: name.trim(), duration_min: Number(mins),
            cash_price: price === '' ? null : Number(price), sport,
          }),
        }));
      haptic.success();
      await alert(t('co.set.savedShort'));
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'sport_locked' ? t('co.set.sportLocked') : t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <PinnedSheet title={type ? type.name : t('co.set.newSession')} onClose={onClose}
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('co.set.close')}</button>
        <button className="savebtn" style={{ marginTop: 0 }} disabled={!ready || busy} onClick={save}>{t('co.set.save')}</button>
      </>}>
      {!type && <Field label={t('co.set.name')} value={name} onChange={setName} type="text" />}
      <Field label={t('co.set.pricePerSession')} value={price} onChange={setPrice} suffix="€" />
      <Field label={t('co.set.length')} value={mins} onChange={setMins} suffix={t('co.set.minutes')} />
      {/* Which skills its lessons are rated on. Fixed once it has lessons. */}
      {/* Only for a coach who teaches both: a tennis-only coach's types are all tennis. */}
      {sports.length > 1 && (<>
        <div className="mf__label" style={{ marginTop: 12 }}>{t('co.set.sport')}</div>
        <div className="bseg" role="radiogroup" aria-label={t('co.set.sport')} style={{ margin: '6px 0 0' }}>
          {(['padel', 'tennis'] as const).map((x) => (
            <button key={x} role="radio" aria-checked={sport === x} disabled={!!type?.used && x !== sport}
              onClick={() => { haptic.tap(); setSport(x); }}>
              {t(x === 'padel' ? 'co.sport.padel' : 'co.sport.tennis')}
            </button>
          ))}
        </div>
        {type?.used && <div className="card__when" style={{ marginTop: 6 }}>{t('co.set.sportLocked')}</div>}
      </>)}
      <div className="card__when" style={{ marginTop: 10 }}>{t('co.set.priceNote')}</div>
    </PinnedSheet>
  );
}

function PackageSheet({ pkg, onClose, onSaved }: {
  pkg: Pkg | null; onClose: () => void; onSaved: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(pkg?.name ?? '');
  const [credits, setCredits] = useState(pkg ? String(pkg.credits) : '');
  const [price, setPrice] = useState(pkg ? String(pkg.price) : '');
  const [validity, setValidity] = useState(pkg?.validity_days == null ? '' : String(pkg.validity_days));
  const [busy, setBusy] = useState(false);
  const per = Number(credits) > 0 ? (Number(price) / Number(credits)).toFixed(0) : '—';
  const ready = name.trim() !== '' && Number(credits) > 0 && price !== '';

  async function save() {
    setBusy(true);
    const body = JSON.stringify({
      name, credits: Number(credits), price: Number(price),
      validity_days: validity === '' ? null : Number(validity),
    });
    try {
      // The same fields either way: a new package is a POST, an existing one a PUT.
      await (pkg ? api(`/api/coach/packages/${pkg.id}`, { method: 'PUT', body })
        : api('/api/coach/packages', { method: 'POST', body }));
      haptic.success();
      await alert(t('co.set.savedShort'));
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <PinnedSheet title={pkg ? pkg.name : t('co.set.newPackage')} onClose={onClose}
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('co.set.close')}</button>
        <button className="savebtn" style={{ marginTop: 0 }} disabled={!ready || busy} onClick={save}>{t('co.set.save')}</button>
      </>}>
      <Field label={t('co.set.name')} value={name} onChange={setName} type="text" />
      <Field label={t('co.set.sessions')} value={credits} onChange={setCredits} />
      <Field label={t('co.set.price')} value={price} onChange={setPrice} suffix="€" />
      <Field label={t('co.set.validFor')} value={validity} onChange={setValidity} suffix={t('co.set.days')} />
      <div className="card__when" style={{ marginTop: 6 }}>
        {t('co.set.perSessionPrice', { amount: per })}
        {validity === '' && t('co.set.neverExpires')}
        {pkg && pkg.sold > 0 && t('co.set.sold', { n: pkg.sold })}
      </div>
    </PinnedSheet>
  );
}

function PricesScreen({ data, onBack, onSaved }: { data: Data; onBack: () => void; onSaved: () => void }) {
  const t = useT();
  const [type, setType] = useState<SessionType | 'new' | null>(null);
  const [pkg, setPkg] = useState<Pkg | 'new' | null>(null);
  const types = data.sessionTypes.filter((x) => x.kind !== 'group');
  const sports = data.coach.sports ?? ['padel'];
  const tennis = sports.includes('tennis');
  const [switching, setSwitching] = useState(false);

  /** «Я провожу и занятия по теннису»: on adds the tennis skills (coach/sports.ts). */
  async function setTennis(on: boolean) {
    setSwitching(true);
    try {
      await api('/api/coach/sports', { method: 'PUT', body: JSON.stringify({ tennis: on }) });
      haptic.success();
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'tennis_types_active' ? t('co.set.tennisBusy')
        : err.message === 'last_sport' ? t('co.set.lastSport') : t('co.failed', { message: err.message }));
    } finally { setSwitching(false); }
  }

  return (
    <SubScreen title={t('co.set.rowPrices')} lead={t('co.set.pricesLead')} onBack={onBack}>
      <div className="list">
        <label className="card checkline">
          <input type="checkbox" checked={tennis} disabled={switching} onChange={(e) => setTennis(e.target.checked)} />
          <span>{t('co.set.tennisOn')}<small>{t('co.set.tennisHint')}</small></span>
        </label>
      </div>
      <div className="section-title">{t('co.set.titlePrices')}</div>
      <div className="list">
        <div className="card setrows">
          {types.map((x) => (
            <button key={x.id} className="prrow" onClick={() => { haptic.tap(); setType(x); }}>
              <span className="prrow__main">
                <b>{x.name}</b>
                <small>{t('co.set.minutesN', { n: x.duration_min })}{sports.length > 1 && ` · ${t(x.sport === 'tennis' ? 'co.sport.tennis' : 'co.sport.padel')}`}</small>
              </span>
              <span className="prrow__price">
                {x.cash_price == null ? t('co.set.noPrice') : `${x.cash_price} €`}
              </span>
              <span className="setrow__chev" aria-hidden="true">›</span>
            </button>
          ))}
          <button className="prrow prrow--add" onClick={() => { haptic.tap(); setType('new'); }}>
            {t('co.set.addSession')}
          </button>
        </div>
        <div className="card__when" style={{ padding: '6px 2px' }}>{t('co.set.priceNote')}</div>
      </div>

      <div className="section-title">{t('co.set.titlePackages')}</div>
      <div className="list">
        <div className="card setrows">
          {data.packages.map((p) => (
            <button key={p.id} className="prrow" onClick={() => { haptic.tap(); setPkg(p); }}>
              <span className="prrow__main">
                <b>{p.name}</b>
                <small>
                  {t('co.set.creditsN', { n: p.credits, sessions: t.word('co.set.sessionsW', p.credits) })}
                  {p.validity_days != null && ` · ${t('co.set.daysN', { n: p.validity_days, days: t.word('co.days', p.validity_days) })}`}
                  {p.sold > 0 && t('co.set.sold', { n: p.sold })}
                </small>
              </span>
              <span className="prrow__price">{p.price} €</span>
              <span className="setrow__chev" aria-hidden="true">›</span>
            </button>
          ))}
          <button className="prrow prrow--add" onClick={() => { haptic.tap(); setPkg('new'); }}>
            {t('co.set.addPackage')}
          </button>
        </div>
      </div>

      {type && <SessionTypeSheet type={type === 'new' ? null : type} sports={sports} onClose={() => setType(null)}
        onSaved={() => { setType(null); onSaved(); }} />}
      {pkg && <PackageSheet pkg={pkg === 'new' ? null : pkg} onClose={() => setPkg(null)}
        onSaved={() => { setPkg(null); onSaved(); }} />}
    </SubScreen>
  );
}

// ---------------------------------------------------------------------
//  The rest of the screens
// ---------------------------------------------------------------------

/** Booking, cancellations and match reviews: one save, as before. */
function RulesScreen({ data, onBack, onSaved }: { data: Data; onBack: () => void; onSaved: () => void }) {
  const t = useT();
  const coach = data.coach;
  const [cutoff, setCutoff] = useState(String(coach.cancel_cutoff_hours));
  // «Напоминания» (sql/050): by the clock, not «за N часов».
  const [remCutoff, setRemCutoff] = useState(coach.reminder_cutoff ?? '13:00');
  const [remEvening, setRemEvening] = useState(coach.reminder_evening_at ?? '20:00');
  const [remDay, setRemDay] = useState(coach.reminder_day_at ?? '10:00');
  const [warn, setWarn] = useState(String(coach.cancel_warning_hours ?? 24));
  const [notice, setNotice] = useState(String(coach.min_notice_hours));
  const [horizon, setHorizon] = useState(String(coach.booking_horizon_days));
  const [review, setReview] = useState(coach.match_review_enabled ?? false);
  const [reviewFee, setReviewFee] = useState(String(coach.match_review_fee ?? 0));
  const [busy, setBusy] = useState(false);

  const dirty = cutoff !== String(coach.cancel_cutoff_hours) ||
    remCutoff !== (coach.reminder_cutoff ?? '13:00') || remEvening !== (coach.reminder_evening_at ?? '20:00') ||
    remDay !== (coach.reminder_day_at ?? '10:00') ||
    warn !== String(coach.cancel_warning_hours ?? 24) ||
    notice !== String(coach.min_notice_hours) || horizon !== String(coach.booking_horizon_days) ||
    review !== (coach.match_review_enabled ?? false) ||
    reviewFee !== String(coach.match_review_fee ?? 0);
  const lastCallTooLate = Number(warn) <= Number(cutoff);
  // As the server and the column check: both times 09:00–20:59, the on-the-day one before the cut-off.
  const okTime = (v: string) => /^\d{2}:\d{2}$/.test(v) && v >= '09:00' && v < '21:00';
  const remindQuiet = !okTime(remEvening) || !okTime(remDay);
  const remindAfterCutoff = !remindQuiet && remDay >= remCutoff;

  async function save() {
    setBusy(true);
    try {
      await api('/api/coach/policy', {
        method: 'PUT',
        body: JSON.stringify({
          cancel_cutoff_hours: Number(cutoff),
          reminder_cutoff: remCutoff,
          reminder_evening_at: remEvening,
          reminder_day_at: remDay,
          cancel_warning_hours: Number(warn),
          min_notice_hours: Number(notice),
          booking_horizon_days: Number(horizon),
          match_review_enabled: review,
          match_review_fee: Number(reviewFee),
        }),
      });
      haptic.success();
      await alert(t('co.set.savedShort'));
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <SubScreen title={t('co.set.titleRules')} onBack={onBack}>
      <div className="list">
        <div className="card setgroup">
          <div className="setgroup__title">{t('co.set.groupBooking')}</div>
          <Field label={t('co.set.minNotice')} hint={t('co.set.minNoticeHint')}
            value={notice} onChange={setNotice} suffix={t('co.set.hours')} />
          <Field label={t('co.set.window')} hint={t('co.set.windowHint')}
            value={horizon} onChange={setHorizon} suffix={t('co.set.days')} />
        </div>

        <div className="card setgroup">
          <div className="setgroup__title">{t('co.set.groupCancellations')}</div>
          <Field label={t('co.set.freeCancel')} hint={t('co.set.freeCancelHint')}
            value={cutoff} onChange={setCutoff} suffix={t('co.set.hBefore')} />
          <Field label={t('co.set.lastCall')} hint={t('co.set.lastCallHint')}
            value={warn} onChange={setWarn} suffix={t('co.set.hBefore')} />
          <CancelWindow cutoff={Number(cutoff)} warn={Number(warn)} />
          {lastCallTooLate && <div className="warnline">{t('co.set.lastCallTooLate', { n: cutoff })}</div>}
        </div>

        <div className="card setgroup">
          <div className="setgroup__title">{t('co.set.groupReminders')}</div>
          <Field type="time" label={t('co.set.remCutoff')} hint={t('co.set.remCutoffHint')}
            value={remCutoff} onChange={setRemCutoff} />
          <Field type="time" label={t('co.set.remEvening')} hint={t('co.set.remEveningHint', { time: remCutoff })}
            value={remEvening} onChange={setRemEvening} />
          <Field type="time" label={t('co.set.remDay')} hint={t('co.set.remDayHint', { time: remCutoff })}
            value={remDay} onChange={setRemDay} />
          <div className="card__when">{t('co.set.quietHours')}</div>
          {remindQuiet && <div className="warnline">{t('co.set.remInQuiet')}</div>}
          {remindAfterCutoff && <div className="warnline">{t('co.set.remDayAfterCutoff', { time: remCutoff })}</div>}
        </div>

        <div className="card setgroup">
          <div className="setgroup__title">{t('co.set.groupExtras')}</div>
          <Toggle label={t('co.set.reviews')} on={review} onChange={setReview} hint={t('co.set.reviewsHint')} />
          {review && <Field label={t('co.set.reviewPrice')} value={reviewFee} onChange={setReviewFee} suffix={t('co.set.each')} />}
        </div>

        {dirty && (
          <button className="savebtn" disabled={busy || lastCallTooLate || remindQuiet || remindAfterCutoff} onClick={save}>
            {busy ? t('co.set.saving') : t('co.set.saveChanges')}
          </button>
        )}
      </div>
    </SubScreen>
  );
}

/** What the court costs, which hours are held, and whether to sell without one. */
function CourtScreen({ data, onBack, onSaved }: { data: Data; onBack: () => void; onSaved: () => void }) {
  const t = useT();
  const [court, setCourt] = useState(String(data.coach.court_cost_per_hour ?? 0));
  const [grid, setGrid] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = court !== String(data.coach.court_cost_per_hour ?? 0);

  async function save() {
    setBusy(true);
    try {
      await api('/api/coach/policy', { method: 'PUT', body: JSON.stringify({ court_cost_per_hour: Number(court) }) });
      haptic.success();
      await alert(t('co.set.savedShort'));
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <SubScreen title={t('co.set.rowCourt')} onBack={onBack}>
      <div className="list">
        <div className="card setgroup">
          <Field label={t('co.set.courtHire')} hint={t('co.set.courtHireHint')}
            value={court} onChange={setCourt} suffix={t('co.set.perHour')} />
          {dirty && (
            <button className="savebtn" disabled={busy} onClick={save}>
              {busy ? t('co.set.saving') : t('co.set.save')}
            </button>
          )}
        </div>
      </div>

      {/* The hours the court costs more, right under what it costs. */}
      <CourtBands bands={data.courtBands ?? []} base={Number(data.coach.court_cost_per_hour ?? 0)}
        week1={data.coach.court_week1_start ?? null} onChanged={onSaved} />

      <div className="list">
        <div className="card">
          <button className="savebtn" style={{ marginTop: 0 }} onClick={() => setGrid(true)}>
            {t('co.set.setCourts')}
          </button>
          <label className="checkline" style={{ marginTop: 12 }}>
            <input type="checkbox" defaultChecked={data.coach.require_court === true}
              onChange={async (e) => {
                await api('/api/coach/policy', {
                  method: 'PUT', body: JSON.stringify({ require_court: e.target.checked }),
                });
                haptic.success();
              }} />
            <span>
              {t('co.set.onlyWithCourt')}
              <small>{t('co.set.onlyWithCourtHint')}</small>
            </span>
          </label>
        </div>
      </div>

      {grid && <CourtGrid onClose={() => { setGrid(false); onSaved(); }} />}
    </SubScreen>
  );
}

/** Who gets in, and how clients are grouped. */
function ClientsScreen({ data, tags, onBack, onSaved, openTags }: {
  data: Data; tags: number | null; onBack: () => void; onSaved: () => void; openTags: boolean;
}) {
  const t = useT();
  const [approveNew, setApproveNew] = useState(data.coach.approve_new_clients ?? true);
  const [invites, setInvites] = useState(data.coach.clients_can_invite ?? false);
  const [sheet, setSheet] = useState(openTags);
  const [busy, setBusy] = useState(false);
  const dirty = approveNew !== (data.coach.approve_new_clients ?? true) ||
    invites !== (data.coach.clients_can_invite ?? false);

  async function save() {
    setBusy(true);
    try {
      await api('/api/coach/policy', {
        method: 'PUT',
        body: JSON.stringify({ approve_new_clients: approveNew, clients_can_invite: invites }),
      });
      haptic.success();
      await alert(t('co.set.savedShort'));
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  return (
    <SubScreen title={t('co.set.rowClients')} onBack={onBack}>
      <div className="list">
        <div className="card setgroup">
          <Toggle label={t('co.set.approveNew')} on={approveNew} onChange={setApproveNew} hint={t('co.set.approveNewHint')} />
          <Toggle label={t('co.set.canInvite')} on={invites} onChange={setInvites} hint={t('co.set.canInviteHint')} />
          {dirty && (
            <button className="savebtn" disabled={busy} onClick={save}>
              {busy ? t('co.set.saving') : t('co.set.save')}
            </button>
          )}
        </div>
        <div className="card setrows">
          <SetRow id="setup-tags" icon="🏷️"
            title={tags === null ? t('co.set.titleTags') : t('co.set.tagsCount', { n: tags })}
            status={t('co.set.tagsStatus')} onOpen={() => setSheet(true)} />
        </div>
      </div>
      {sheet && <TagsSheet onClose={() => { setSheet(false); onSaved(); }} />}
    </SubScreen>
  );
}

/** «Постоянные клиенты» as its own screen, reached from the Клиенты group. */
function RegularsScreen({ data, onBack, onSaved }: {
  data: Data; onBack: () => void; onSaved: () => void;
}) {
  const t = useT();
  return (
    <SubScreen title={t('co.reg.rowTitle')} lead={t('co.reg.setLead')} onBack={onBack}>
      <div className="list">
        <RegularsSettings coach={data.coach} onSaved={onSaved} />
      </div>
    </SubScreen>
  );
}

/**
 * «Постоянные»: how early to warn, who gets told, and in whose words.
 *
 * Three settings and one line. The message to the client is written by
 * the app except for one paragraph, and that paragraph is edited right
 * where it sits in the bubble — the app's lines greyed out around it.
 * The preview is in the coach's language; each client gets theirs, and
 * an edited line goes to all of them as written.
 */
function RegularsSettings({ coach, onSaved }: { coach: Data['coach']; onSaved: () => void }) {
  const t = useT();
  const [days, setDays] = useState(Number(coach.regular_runway_days ?? 21));
  const [ahead, setAhead] = useState<number | null>(coach.regular_horizon_days ?? null);
  const [mode, setMode] = useState<'off' | 'manual' | 'auto'>(coach.regular_nudge_mode ?? 'manual');
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState<Awaited<ReturnType<typeof nudgeSample>> | null>(null);
  /** What the field holds: the coach's own line, or the default one until he changes it. */
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    let gone = false;
    nudgeSample()
      .then((p) => { if (!gone) { setSample(p); setDraft(coach.regular_nudge_text ?? p.defaultBody); } })
      .catch(() => { if (!gone) setSample(null); });
    return () => { gone = true; };
  }, [coach.regular_nudge_text]);

  async function save(patch: Record<string, unknown>) {
    setBusy(true);
    try {
      await api('/api/coach/policy', { method: 'PUT', body: JSON.stringify(patch) });
      haptic.success();
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  const line = (draft ?? '').trim();
  const stored = (coach.regular_nudge_text ?? sample?.defaultBody ?? '').trim();
  const dirty = sample !== null && line !== stored;
  // The default line, word for word, is no edit: stored as «none», so each
  // client keeps getting it in their own language.
  const toSave = () => (!line || line === sample?.defaultBody ? null : line);
  const changed = sample !== null && line !== sample.defaultBody;

  return (
    <div className="card setgroup">
      {/* Один сегмент решает, кто кому пишет. «Вручную» по умолчанию:
          бот зовёт тренера, а не пишет за него. */}
      <div className="sethead"><b>{t('co.reg.mode')}</b></div>
      <div className="bseg" role="radiogroup" aria-label={t('co.reg.mode')}>
        {([['off', 'co.reg.modeOff'], ['manual', 'co.reg.modeManual'], ['auto', 'co.reg.modeAuto']] as const)
          .map(([id, key]) => (
            <button key={id} role="radio" aria-checked={mode === id} disabled={busy}
              onClick={() => { haptic.tap(); setMode(id); void save({ regular_nudge_mode: id }); }}>
              {t(key)}
            </button>
          ))}
      </div>
      <div className="sethint" aria-live="polite">
        {t(mode === 'off' ? 'co.reg.modeOffHint'
          : mode === 'auto' ? 'co.reg.modeAutoHint' : 'co.reg.modeManualHint')}
      </div>

      <div className="sethead" style={{ marginTop: 12 }}>
        <b>{t('co.reg.setDays')}</b>
        <small>{t('co.reg.setDaysHint')}</small>
      </div>
      <div className="bseg" role="radiogroup" aria-label={t('co.reg.setDays')}>
        {[7, 14, 21, 28].map((n) => (
          <button key={n} role="radio" aria-checked={days === n} disabled={busy}
            onClick={() => { haptic.tap(); setDays(n); void save({ regular_runway_days: n }); }}>
            {t('co.reg.dayChip', { n })}
          </button>
        ))}
      </div>

      {/* How far ahead ★ regulars may book (sql/044); clients never see ★. */}
      <div className="sethead" style={{ marginTop: 12 }}>
        <b>{t('co.reg.horizon')}</b>
        <small>{t('co.reg.horizonHint', { n: Math.round(Number(coach.booking_horizon_days) / 7 * 10) / 10 })}</small>
      </div>
      <div className="bseg" role="radiogroup" aria-label={t('co.reg.horizon')}>
        {([null, 21, 28, 42, 56, 84] as const).map((n) => (
          <button key={String(n)} role="radio" aria-checked={ahead === n} disabled={busy}
            onClick={() => { haptic.tap(); setAhead(n); void save({ regular_horizon_days: n }); }}>
            {n === null ? t('co.reg.horizonSame') : t('co.reg.weeksChip', { n: n / 7 })}
          </button>
        ))}
      </div>

      {/* Тренер правит одну строку — там, где она окажется. */}
      {sample && (
        <>
          <div className="sethint" style={{ marginTop: 14 }}>{t('co.reg.previewLine')}</div>
          <NudgeBubble parts={sample}>
            <GrowArea className="tgmsg__edit" rows={1} maxLength={300}
              aria-label={t('co.reg.previewLine')}
              value={draft ?? ''} onChange={(e) => setDraft(e.target.value)} />
          </NudgeBubble>
          <div className="sethint">{t('co.reg.textAsIs')}</div>
          {(changed || dirty) && (
            <div className="msgprev__row">
              {changed ? (
                <button className="quietlink" disabled={busy}
                  onClick={() => {
                    haptic.tap();
                    setDraft(sample.defaultBody);
                    if (coach.regular_nudge_text !== null && coach.regular_nudge_text !== undefined)
                      void save({ regular_nudge_text: null });
                  }}>
                  {t('co.reg.setReset')}
                </button>
              ) : <span />}
              {dirty && (
                <button className="savebtn" style={{ width: 'auto', marginTop: 0 }} disabled={busy}
                  onClick={() => void save({ regular_nudge_text: toSave() })}>
                  {t('co.reg.setSave')}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** What the coach rates, the drills they keep, and the evening brief. */
function AiScreen({ axes, onBack, onSaved }: { axes: number | null; onBack: () => void; onSaved: () => void }) {
  const t = useT();
  const [sheet, setSheet] = useState<'axes' | 'drills' | null>(null);
  return (
    <SubScreen title={t('co.set.rowAi')} lead={t('co.set.aiLead')} onBack={onBack}>
      <div className="list">
        <div className="card setrows">
          <SetRow id="setup-axes" icon="🎯"
            title={axes === null ? t('co.set.titleAxes') : t('co.set.axesCount', { n: axes })}
            status={t('co.set.axesStatus')} onOpen={() => setSheet('axes')} />
          <SetRow id="setup-drills" icon="🏋️" title={t('dr.title')} status={t('dr.rowStatus')}
            onOpen={() => setSheet('drills')} />
          <BriefRow />
        </div>
      </div>
      {sheet === 'axes' && <AxesSheet onClose={() => { setSheet(null); onSaved(); }} />}
      {sheet === 'drills' && <DrillLibrarySheet onClose={() => setSheet(null)} />}
    </SubScreen>
  );
}

/** The link clients book through, and the one place it is copied from. */
function LinkScreen({ steps, onBack, onShared }: {
  steps: FirstSteps | null; onBack: () => void; onShared: () => void;
}) {
  const t = useT();
  const [copied, setCopied] = useState<string | null>(null);
  const link = steps?.link ?? null;
  // A coach who teaches tennis too has two: the client who joins through
  // the tennis one is a tennis client (coach/sports.ts).
  const tennisLink = steps?.tennisLink ?? null;

  async function copy(url: string) {
    try { await navigator.clipboard.writeText(url); } catch { /* an old WebView: the text is on screen to copy by hand */ }
    haptic.success();
    setCopied(url);
    // The same call «Первые шаги» makes: this is what ticks that step off.
    api('/api/coach/first-steps/link-shared', { method: 'POST', body: '{}' }).then(onShared).catch(() => {});
  }
  const card = (url: string | null, label?: string) => (
    <>
      {label && <div className="section-title">{label}</div>}
      <div className="list">
        <div className="card">
          {url === null ? <div className="skeleton" /> : <>
            <code className="fs-link__url">{url}</code>
            <button className="savebtn fs-link__copy" onClick={() => copy(url)}>
              {copied === url ? t('fs.copied') : t('fs.copy')}
            </button>
          </>}
        </div>
      </div>
    </>
  );

  return (
    <SubScreen title={t('co.set.rowLink')} lead={t('fs.linkHint')} onBack={onBack}>
      {tennisLink ? <>
        {card(link, t('co.set.linkPadel'))}
        {card(tennisLink, t('co.set.linkTennis'))}
      </> : card(link)}
    </SubScreen>
  );
}

function LanguageScreen({ onBack }: { onBack: () => void }) {
  const t = useT();
  return (
    <SubScreen title={t('co.set.titleLanguage')} onBack={onBack}>
      <div className="list">
        <div className="card setlang">
          <div className="setlang__row">
            <span className="setlang__label">{t('co.set.appLanguage')}</span>
            <LanguageSwitch />
          </div>
          <div className="card__when" style={{ padding: '2px 2px 0' }}>{t('co.set.languageHint')}</div>
        </div>
      </div>
    </SubScreen>
  );
}

// ---------------------------------------------------------------------
//  The first-run checklist
// ---------------------------------------------------------------------

interface Step { key: Screen | 'profile'; label: string; done: boolean; value?: string }

function Checklist({ steps, onGo }: { steps: Step[]; onGo: (s: Step) => void }) {
  const t = useT();
  const done = steps.filter((s) => s.done).length;
  const left = steps.length - done;
  const next = steps.findIndex((s) => !s.done);
  return (
    <div className="chklist">
      <div className="chklist__head">
        <b>{t('co.set.chkLeft', { n: left, steps: t.word('co.set.stepsW', left) })}</b>
        <span>{t('co.set.chkOf', { done, total: steps.length })}</span>
      </div>
      <div className="chklist__bar"><i style={{ width: `${(done / steps.length) * 100}%` }} /></div>
      <div className="chklist__steps">
        {steps.map((s, i) => (
          <button key={s.key} className={`chkstep${s.done ? ' chkstep--done' : i === next ? ' chkstep--next' : ''}`}
            onClick={() => { haptic.tap(); onGo(s); }}>
            <i>{s.done ? '✓' : i + 1}</i>
            <span>{s.label}</span>
            {s.done && s.value && <small>{s.value}</small>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
//  The menu
// ---------------------------------------------------------------------

type Screen = 'hours' | 'prices' | 'rules' | 'court' | 'clients' | 'regulars' | 'ai' | 'calendar' | 'link' | 'lang';

/**
 * Set the first time all six steps are done, and the checklist never
 * returns. Kept per coach: two coaches sharing a phone each get their own
 * opening, and so does the same coach on a new device.
 */
const setupDoneKey = (slug?: string) => `pd.settings.setupDone:${slug ?? '?'}`;

export function Settings({ scrollToTags, billing }: {
  scrollToTags?: number; billing?: Billing | null;
} = {}) {
  const t = useT();
  const [data, setData] = useState<Data | null>(null);
  const [screen, setScreen] = useState<Screen | null>(null);
  const [infoSheet, setInfoSheet] = useState(false);
  const [openTags, setOpenTags] = useState(false);
  const [info, setInfo] = useState<CoachView | null>(null);
  const [steps, setSteps] = useState<FirstSteps | null>(null);
  const [counts, setCounts] = useState<{ axes: number | null; tags: number | null }>({ axes: null, tags: null });

  const load = () => api<Data>('/api/coach/settings').then(setData);
  const loadRows = () => {
    loadCoachInfo().then(setInfo).catch(() => setInfo(null));
    api<FirstSteps>('/api/coach/first-steps').then(setSteps).catch(() => setSteps(null));
    api<any[]>('/api/rating-axes').then((a) => setCounts((c) => ({ ...c, axes: a.length }))).catch(() => {});
    api<any[]>('/api/coach/groups').then((g) => setCounts((c) => ({ ...c, tags: g.length }))).catch(() => {});
  };
  useEffect(() => { load().catch(() => setData(null)); loadRows(); }, []);

  // Coming from "Edit tags" on the clients list: straight to that screen.
  useEffect(() => {
    if (scrollToTags) { setOpenTags(true); setScreen('clients'); }
  }, [scrollToTags]);

  // «Первые шаги» on the Calendar tab sends people here by name.
  useEffect(() => {
    const on = (e: Event) => {
      const { tab, anchor } = (e as CustomEvent<{ tab: string; anchor?: string }>).detail ?? {};
      if (tab !== 'settings') return;
      if (anchor === 'set-hours') setScreen('hours');
      if (anchor === 'set-prices') setScreen('prices');
    };
    window.addEventListener('coach:go', on);
    return () => window.removeEventListener('coach:go', on);
  }, []);

  if (!data) return <div className="list"><div className="skeleton" /><div className="skeleton" /></div>;

  const refresh = () => { load().catch(() => {}); loadRows(); };
  const back = () => setScreen(null);

  if (screen === 'hours') return <HoursScreen data={data} onBack={back} onSaved={refresh} />;
  if (screen === 'prices') return <PricesScreen data={data} onBack={back} onSaved={refresh} />;
  if (screen === 'rules') return <RulesScreen data={data} onBack={back} onSaved={refresh} />;
  if (screen === 'court') return <CourtScreen data={data} onBack={back} onSaved={refresh} />;
  if (screen === 'clients') return (
    <ClientsScreen data={data} tags={counts.tags} openTags={openTags}
      onBack={() => { setOpenTags(false); back(); }} onSaved={refresh} />
  );
  if (screen === 'regulars') return <RegularsScreen data={data} onBack={back} onSaved={refresh} />;
  if (screen === 'ai') return <AiScreen axes={counts.axes} onBack={back} onSaved={refresh} />;
  if (screen === 'calendar') return (
    <SubScreen title={t('co.set.rowCalendar')} onBack={back}><SendToCalendar /></SubScreen>
  );
  if (screen === 'link') return <LinkScreen steps={steps} onBack={back} onShared={loadRows} />;
  if (screen === 'lang') return <LanguageScreen onBack={back} />;

  // ---- what is set, and what is still missing ----
  const coach = data.coach;
  const priced = data.sessionTypes.find((x) => x.kind !== 'group' && x.cash_price != null);
  const hoursLine = summariseHours(data.hours.map((h) => ({
    weekday: h.weekday, start_time: h.start_time, end_time: h.end_time,
  })));
  const courtCost = Number(coach.court_cost_per_hour ?? 0);
  // «пик 30 €/час»: the dearest band, which is what a coach wants to see.
  const peak = (data.courtBands ?? []).reduce((m: number, b: any) => Math.max(m, Number(b.price_per_hour)), 0);
  const peakLine = peak > 0 ? t('co.set.sumPeak', { price: t('co.set.perHourN', { n: peak }) }) : null;
  const address = (info?.address ?? '').trim();
  const linkShared = steps?.steps.link === true;

  const todo = {
    hours: hoursLine === null,
    price: !priced,
    court: !(courtCost > 0),
    address: address === '',
    link: steps !== null && !linkShared,
  };
  // Shown while any of the five is still missing, and gone for good after.
  const checklist: Step[] = [
    { key: 'hours', label: t('co.set.chkHours'), done: !todo.hours, value: hoursLine ?? undefined },
    { key: 'prices', label: t('co.set.chkPrice'), done: !todo.price, value: priced ? `${priced.cash_price} €` : undefined },
    { key: 'court', label: t('co.set.chkCourt'), done: !todo.court, value: courtCost > 0 ? t('co.set.perHourN', { n: courtCost }) : undefined },
    { key: 'profile', label: t('co.set.chkAddress'), done: !todo.address, value: address ? address.split('\n')[0] : undefined },
    { key: 'link', label: t('co.set.chkLink'), done: !todo.link },
  ];
  const stepsLoaded = steps !== null && info !== null;
  // Gone for good once all six are done: a coach who later clears a field
  // is not walked through the opening again.
  const doneKey = setupDoneKey(steps?.slug);
  const everDone = (() => { try { return localStorage.getItem(doneKey) === '1'; } catch { return false; } })();
  const left = checklist.some((s) => !s.done);
  if (stepsLoaded && !left && !everDone) { try { localStorage.setItem(doneKey, '1'); } catch { /* a private window: it shows once more */ } }
  const showChecklist = stepsLoaded && left && !everDone;

  const prices = [
    priced ? t('co.set.minutesN', { n: priced.duration_min }) : null,
    priced ? `${priced.cash_price} €` : t('co.set.noPrice'),
    data.packages.length ? t('co.set.pkgsN', { n: data.packages.length, pkgs: t.word('co.set.pkgsW', data.packages.length) }) : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="setmenu">
      {showChecklist && (
        <Checklist steps={checklist} onGo={(s) => {
          if (s.key === 'profile') setInfoSheet(true);
          else setScreen(s.key as Screen);
        }} />
      )}

      <div className="section-title">{t('co.set.menuMe')}</div>
      <div className="list">
        <div className="card setrows">
          <SetRow id="set-lang" icon="🌐" title={t('co.set.titleLanguage')}
            value={getLang() === 'ru' ? 'Русский' : 'English'} onOpen={() => setScreen('lang')} />
          <InfoRow view={info} onOpen={() => setInfoSheet(true)} dot={todo.address} />
          <SetRow id="set-link" icon="🔗" title={t('co.set.rowLink')} dot={todo.link}
            status={linkShared ? steps?.link ?? t('co.set.sumLinkSent') : t('co.set.sumLinkNone')}
            onOpen={() => setScreen('link')} />
        </div>
      </div>

      <div className="section-title">{t('co.set.menuWork')}</div>
      <div className="list">
        <div className="card setrows">
          <SetRow id="set-hours" icon="🕗" title={t('co.set.titleHours')} dot={todo.hours}
            status={hoursLine ?? t('co.set.sumHoursNone')} onOpen={() => setScreen('hours')} />
          <SetRow id="set-prices" icon="💶" title={t('co.set.rowPrices')} dot={todo.price}
            status={prices} onOpen={() => setScreen('prices')} />
          <SetRow id="set-rules" icon="📋" title={t('co.set.titleRules')}
            status={t('co.set.sumRules', { cutoff: coach.cancel_cutoff_hours, days: coach.booking_horizon_days })}
            onOpen={() => setScreen('rules')} />
          <SetRow id="set-court" icon="🎾" title={t('co.set.rowCourt')} dot={todo.court}
            status={courtCost > 0
              ? [t('co.set.perHourN', { n: courtCost }), peakLine].filter(Boolean).join(' · ')
              : t('co.set.sumCourtNone')}
            onOpen={() => setScreen('court')} />
        </div>
      </div>

      <div className="section-title">{t('co.set.menuClients')}</div>
      <div className="list">
        <div className="card setrows">
          <SetRow id="set-clients" icon="🏷️" title={t('co.set.rowClients')}
            status={[
              (coach.approve_new_clients ?? true) ? t('co.set.approveManual') : t('co.set.approveAuto'),
              counts.tags === null ? null : t('co.set.tagsN', { n: counts.tags, tags: t.word('co.set.tagsW', counts.tags) }),
            ].filter(Boolean).join(' · ')}
            onOpen={() => setScreen('clients')} />
          <SetRow id="set-regulars" icon="📌" title={t('co.reg.rowTitle')}
            status={[
              t('co.reg.rowDays', { n: coach.regular_runway_days ?? 21,
                days: t.word('co.days', Number(coach.regular_runway_days ?? 21)) }),
              t(coach.regular_nudge_mode === 'off' ? 'co.reg.modeOff'
                : coach.regular_nudge_mode === 'auto' ? 'co.reg.modeAuto' : 'co.reg.modeManual').toLowerCase(),
            ].join(' · ')}
            onOpen={() => setScreen('regulars')} />
          <SetRow id="set-ai" icon="✨" title={t('co.set.rowAi')} dot={counts.axes === 0}
            status={counts.axes ? t('co.set.axesN', { n: counts.axes, axes: t.word('co.set.axesW', counts.axes) }) : t('co.set.sumAiNone')}
            onOpen={() => setScreen('ai')} />
        </div>
      </div>

      <div className="section-title">{t('co.set.menuApp')}</div>
      <div className="list">
        <div className="card setrows">
          <SetRow id="set-calendar" icon="📅" title={t('co.set.rowCalendar')}
            value={t('co.set.sumCalendar')} onOpen={() => setScreen('calendar')} />
          {billing && <PlanRow b={billing} inline />}
        </div>
      </div>

      <VersionLine />

      {infoSheet && info && (
        <InfoSheet view={info} onClose={() => { setInfoSheet(false); loadRows(); }} onChanged={setInfo} />
      )}
    </div>
  );
}

/**
 * Which build is running, as /health reports it (src/version.ts): the
 * short commit and when it was built, or «dev». Nothing to tap; it is
 * there to answer "is the new version live?".
 */
function VersionLine() {
  const t = useT();
  const [v, setV] = useState<string | null>(null);
  useEffect(() => {
    fetch(`${BASE}/health`).then((r) => r.json()).then((h: { version?: string }) => setV(h.version ?? null)).catch(() => setV(null));
  }, []);
  if (!v) return null;
  return <div className="set-version">{t('co.set.version', { v })}</div>;
}

function Field({ label, value, onChange, suffix, hint, type = 'number' }: {
  label: string; value: string; onChange: (v: string) => void;
  suffix?: string; hint?: string; type?: string;
}) {
  return (
    <label className="field">
      <span className="field__label">
        {label}
        {hint && <small>{hint}</small>}
      </span>
      <span className="field__input">
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
          inputMode={type === 'number' ? 'decimal' : undefined} />
        {suffix && <em>{suffix}</em>}
      </span>
    </label>
  );
}

/** An on/off setting. A switch, because "On" on a button reads as a label, not a control. */
function Toggle({ label, hint, on, onChange }: {
  label: string; hint?: string; on: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="field">
      <span className="field__label field__label--strong">
        {label}
        {hint && <small>{hint}</small>}
      </span>
      <button type="button" role="switch" aria-checked={on} aria-label={label}
        className="switch" onClick={() => { haptic.tap(); onChange(!on); }}>
        <span className="switch__knob" />
      </button>
    </div>
  );
}

/**
 * The cancellation window, drawn. The numbers alone made coaches work
 * out which side of 12 hours was the free one; the bar shows it.
 */
function CancelWindow({ cutoff, warn }: { cutoff: number; warn: number }) {
  const t = useT();
  // Fixed proportions, not to scale: the free side runs back to the
  // moment of booking, so there is no honest length to draw it at.
  return (
    <div className="cancelwin">
      <div className="cancelwin__bar">
        <span className="cancelwin__free" />
        <span className="cancelwin__paid" />
      </div>
      <div className="cancelwin__legend">
        <span><i className="cancelwin__dot cancelwin__dot--free" />{t('co.set.freeToCancel')}</span>
        <span><i className="cancelwin__dot cancelwin__dot--paid" />{t('co.set.lastHours', { n: cutoff || 0 })}</span>
      </div>
      <div className="cancelwin__story">
        {t('co.set.windowStory', { warn: warn || 0 })}
      </div>
    </div>
  );
}
